import { every, isEmpty, keys } from 'lodash';

import { ClosingTasksInput } from '__generated__/globalTypes';
import { PreContractDetailsFormState } from 'sellDirect/components/closingDash/contract/SetUpPurchaseContractForm';
import { HelloSignTaskFields_signers } from 'sellDirect/helpers/__generated__/HelloSignTaskFields';
import { UsableHoaAmenitiesKey } from 'sellDirect/declarations/Amenities';
import { HoaFeesTaskFields_hoaAmenities } from 'sellDirect/helpers/__generated__/HoaFeesTaskFields';

/**
 * Transform form input from PreContractDetailsForm for mutation updateClosingTasks
 */
export const preContractDetailsFormToTasks = ({
  ownerName,
  secondOwnerName,
  hasHoa,
  hoaAmenities,
  hoaFees,
  hoaName,
  closeDate,
}: PreContractDetailsFormState): ClosingTasksInput => ({
  hoaFees: {
    hasHoa,
    hoaAmenities: {
      clubhouse: hoaAmenities?.clubhouse,
      dogPark: hoaAmenities?.dogPark,
      golf: hoaAmenities?.golf,
      gym: hoaAmenities?.gym,
      other: hoaAmenities?.other,
      otherInfo: hoaAmenities?.otherInfo,
      park: hoaAmenities?.park,
      pool: hoaAmenities?.pool,
      sauna: hoaAmenities?.sauna,
      sportsCourts: hoaAmenities?.sportsCourts,
      tennisCourt: hoaAmenities?.tennisCourt,
      trails: hoaAmenities?.trails,
      trashRecycling: hoaAmenities?.trashRecycling,
    },
    hoaFees: hasHoa ? hoaFees : [],
    hoaName,
  },
  setCloseDate: {
    selectedDate: closeDate,
  },
  setSignatories: {
    signerNames: [ownerName, secondOwnerName].filter((name): name is string => !!name),
  },
});

/**
 * Checks if PreContractDetailsForm input is valid for submission
 */
export const validateContractFormInput = (
  isEdited: boolean,
  formState: PreContractDetailsFormState,
): boolean => {
  const { hasHoa, hoaName, hoaFees, hasSecondOwner, ownerName, secondOwnerName, closeDate } =
    formState;

  let isHoaValid = true;
  if (hasHoa) {
    // only if home has HOA (sometimes they may not)
    isHoaValid = !!hoaName && every(hoaFees, (fee) => !!fee.frequency && fee.amount !== undefined);
  }

  // Validate owner inputs
  const owners = hasSecondOwner ? [ownerName, secondOwnerName] : [ownerName];
  const isOwnersValid =
    (owners && owners?.length > 0 && every(owners, (owner) => !!owner && !!owner.trim())) ?? false;

  return isEdited && hasHoa != null && isHoaValid && isOwnersValid && closeDate != null;
};

/**
 * Filter for the amenities that the HOA has
 */
export const getHoaAmenities = (
  hoaAmenities: HoaFeesTaskFields_hoaAmenities | null,
): UsableHoaAmenitiesKey[] | null => {
  if (isEmpty(hoaAmenities)) {
    return null;
  }
  const allKeys = keys(hoaAmenities) as Array<keyof HoaFeesTaskFields_hoaAmenities>;
  const filteredKeys = allKeys.filter(
    (amenity) =>
      hoaAmenities && hoaAmenities[amenity] && amenity !== '__typename' && amenity !== 'otherInfo',
  );
  return filteredKeys as UsableHoaAmenitiesKey[];
};

/**
 * For combined purchase agreement and repair addendum, check if signing is in progress
 */
export const isPurchaseAgreementInProgress = (
  signers: Array<HelloSignTaskFields_signers | null> | null | undefined,
) => {
  if (!signers || signers?.length === 0) {
    return false;
  }
  return signers?.some((signer) => signer?.signedAt);
};
